import { useState } from 'react'
import { motion } from 'framer-motion'
import { MagneticButton } from '@/components/MagneticButton'
import { SiteNav } from '@/components/ui/site-chrome'
import { SiteFooter } from '@/components/ui/hero-sections'
import { AvatarStack, CommunityCard, EmptyState, SearchInput, SectionHeading, Chip, ParticipationNote } from '@/components/lc'
import { communities } from '@/data/content'
import { useSmoothScroll } from '@/lib/useSmoothScroll'
import { usePrefersReducedMotion } from '@/lib/useReducedMotion'
import { cn } from '@/lib/utils'

/* ================================================================== */
/*  Communities index: the dark directory of all eight circles.        */
/*  Cosmos, Spirituality and Rhythm are live; the rest open soon.      */
/* ================================================================== */

const LIVE: Record<string, string> = {
  Cosmos: '/communities/cosmos',
  Rhythm: '/communities/rhythm',
  Zen: '/communities/zen',
}

/* Communities surfaced as dimmed "coming soon" cards (not yet clickable) */
const COMING_SOON = new Set(['Dance', 'Spirituality'])

/* live cards plus the explicit coming-soon teasers; the rest stay hidden */
const SHOWN = new Set([...Object.keys(LIVE), ...COMING_SOON])

function Backdrop() {
  const reduced = usePrefersReducedMotion()
  const blobs = [
    { cls: 'h-[50rem] w-[50rem] -left-56 top-24 opacity-45', color: '#7C3AED', anim: { x: [0, 80, 0], y: [0, 50, 0] }, d: 26 },
    { cls: 'h-[42rem] w-[42rem] -right-40 top-[18%] opacity-40', color: '#FF6B5C', anim: { x: [0, -70, 0], y: [0, 70, 0] }, d: 33 },
    { cls: 'h-[46rem] w-[46rem] left-[22%] top-[58%] opacity-40', color: '#5B21B6', anim: { x: [0, 60, 0], y: [0, -60, 0] }, d: 39 },
    { cls: 'h-[24rem] w-[24rem] right-[18%] bottom-[6%] opacity-15', color: '#C8F04B', anim: { x: [0, -50, 0], y: [0, -40, 0] }, d: 29 },
  ]
  return (
    <div className="grain grain-on-dark pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #191231 0%, #130E22 55%, #170F2A 100%)' }} />
      {blobs.map((b, i) => (
        <motion.div
          key={i}
          className={cn('blob', b.cls)}
          style={{ background: b.color }}
          animate={reduced ? undefined : b.anim}
          transition={reduced ? undefined : { duration: b.d, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% 38%, transparent 42%, rgba(8,6,16,0.72) 100%)' }} />
      {/* phones: the blurred blobs bloom brighter on a small canvas, washing the
          bg pink and killing text contrast — lay a darker scrim on mobile only */}
      <div className="absolute inset-0 md:hidden" style={{ background: 'rgba(9,6,18,0.5)' }} />
    </div>
  )
}

const FACES = [
  { src: '/img/portrait-1.jpg' }, { src: '/img/portrait-2.jpg' }, { src: '/img/portrait-3.jpg' },
  { src: '/img/portrait-4.jpg' }, { src: '/img/portrait-5.jpg' }, { src: '/img/portrait-6.jpg' },
]

export default function Communities() {
  useSmoothScroll()
  const [query, setQuery] = useState('')
  const q = query.trim().toLowerCase()
  const shown = communities.filter(
    (c) =>
      SHOWN.has(c.name) &&
      (!q || c.name.toLowerCase().includes(q) || c.topic.toLowerCase().includes(q) || c.paths.some((p) => p.toLowerCase().includes(q))),
  )

  return (
    <div className="lc-dark relative min-h-screen overflow-x-clip bg-night-800 text-lc-ink">
      <Backdrop />
      <SiteNav />

      <main className="relative z-10">
        <section className="px-5 pb-12 pt-36 md:pt-44">
          <div className="container-lc grid items-center gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="label text-lc-mute">Three circles to start · more on the way</p>
              <h1 className="mt-4 max-w-3xl font-display text-5xl">
                Pick what <span className="rounded-lg bg-lime px-3 text-night">moves</span> you.
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-lc-mute">
                Every community comes with vetted LifeCrafters, small circles and real events.
                Start with one; wander into others whenever you like.
              </p>
              <SearchInput
                className="mt-8 max-w-md"
                placeholder="Search: yoga, tarot, singing, photography"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="mt-5 flex flex-wrap gap-2">
                {['Astrology', 'Music', 'Yoga'].map((t) => (
                  <button key={t} onClick={() => setQuery(query === t ? '' : t)}>
                    <Chip small tone={q === t.toLowerCase() ? 'lime' : 'outline'} className="transition-colors">{t}</Chip>
                  </button>
                ))}
              </div>
            </div>

            {/* who's already inside */}
            <div className="lg:col-span-5">
              <div className="lc-glass relative overflow-hidden rounded-card p-5 xl:p-7">
                <span className="blob absolute -right-12 -top-12 h-40 w-40 opacity-30" style={{ background: '#7C3AED' }} aria-hidden="true" />
                <div className="relative">
                  <div className="flex items-center justify-between gap-4">
                    <AvatarStack people={FACES} size={40} max={5} />
                    <span className="label !text-[11px] text-lc-mute">and many more</span>
                  </div>
                  <p className="mt-5 font-display text-2xl leading-tight">
                    Neighbours, classmates and stage partners, already inside.
                  </p>
                  <div className="mt-5 space-y-2.5 border-t border-lc-line pt-5">
                    {[
                      ['Every host identity-verified', 'shield'],
                      ['Circles online and near you', 'mapPin'],
                      ['A calm daily practice in every circle', 'sun'],
                    ].map(([line]) => (
                      <p key={line as string} className="flex items-center gap-2.5 text-[14px] text-lc-mute">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime" aria-hidden="true" />
                        {line}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="px-5 pb-24">
          <div className="container-lc">
            {shown.length > 0 ? (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((c) => (
                  <CommunityCard
                    key={c.name}
                    img={c.image}
                    focus={c.focus}
                    name={c.name}
                    topic={c.topic}
                    tagline={c.tagline}
                    to={LIVE[c.name]}
                    soon={!LIVE[c.name]}
                    className="max-w-none"
                  />
                ))}
              </div>
            ) : (
              <EmptyState
                className="mx-auto max-w-md"
                icon="compass"
                title="Nothing by that name yet"
                line={`No circle matches "${query}". Try a broader word, or tell us what's missing.`}
                action={<button onClick={() => setQuery('')} className="rounded-pill bg-lime px-5 py-2.5 text-[14px] font-bold text-night">Show all communities</button>}
              />
            )}
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Chip tone="glass">More circles open through the year</Chip>
              <Chip tone="outline">Suggest a community</Chip>
            </div>
          </div>
        </section>

        <section id="join" className="px-5 pb-28">
          <div className="container-lc">
            <div className="lc-glass relative overflow-hidden rounded-card px-8 py-14 text-center">
              <span className="blob absolute left-1/2 top-0 h-64 w-64 -translate-x-1/2 opacity-40" style={{ background: '#7C3AED' }} aria-hidden="true" />
              <div className="relative">
                <SectionHeading
                  kicker="Free to join"
                  title="One of these already feels like home."
                  intro="Pick the circle that made you pause, sit in on one gathering, and see who you run into."
                  center
                />
                <MagneticButton href="/signup" className="mt-8 inline-flex rounded-pill bg-lime px-6 py-3 text-[15px] xl:px-9 xl:py-4 xl:text-[17px] font-bold text-night">
                  Join a community
                </MagneticButton>
                <ParticipationNote className="mt-6 mx-auto text-center" />
              </div>
            </div>
          </div>
        </section>

        <SiteFooter dark />
      </main>
    </div>
  )
}
